// The Past list, as pure functions: every meeting that has happened, newest
// first and grouped by day, marked only by what it has (notes, open
// actions, a recording on its way or failed: no notes says nothing, and
// an unanswered question from the agent is not the row's business), your
// own calendar blocks left out
// unless asked for, a filter and a search; and the one line above it that
// says how many recent meetings the agent asked about and still have no
// notes (the shared "waits on you" count, so it explains the sidebar's
// number), which the operator can hide until a new one comes.
//
// Nothing here changes what a meeting's state means (status.ts and the
// shared predicates, which the phone and the iPhone fixtures pin): it only
// decides what the list shows and in which words.
//
// No React. Self-test: pastModel.selftest.ts.

import type { Meeting } from "@/api/meetings";
import { meetingAskedRecently, meetingRecordingFailed } from "@/features/attention/predicates";
import { formatDateTime, formatDay, joinList, pluralize, toDate } from "@/lib/format";
import { isBlock, uploadStalled } from "./listModel";
import { peopleIn, type NameBook } from "./people";
import { byStartDesc, meetingTitle, openActionCount, otherAttendees } from "./status";

export type PastFilter = "all" | "notes" | "none" | "actions";

export const PAST_FILTERS: { value: PastFilter; label: string }[] = [
  { value: "all", label: "All meetings" },
  { value: "notes", label: "With notes" },
  { value: "none", label: "No notes" },
  { value: "actions", label: "Open actions" },
];

/** Rows shown at first, and added by each "Show older". */
export const PAST_PAGE = 60;

export const hasNotes = (m: Pick<Meeting, "notes_md">) => !!(m.notes_md ?? "").trim();

/**
 * Has happened (or is being written up): anything not upcoming, except a
 * skipped meeting whose start is still ahead (cancelled in the calendar).
 */
export function isPast(m: Pick<Meeting, "status" | "start_at">, now: Date = new Date()): boolean {
  if (m.status === "upcoming") return false;
  if (m.status !== "skipped") return true;
  const start = toDate(m.start_at);
  return !start || start <= now;
}

/** Every word a search can match: the title and everyone in it, by name and address. */
export function matchesSearch(m: Meeting, q: string, book: NameBook): boolean {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [m.title, ...peopleIn(m, book), ...otherAttendees(m), ...m.attendees.map((a) => a.email)].join(" ").toLowerCase();
  return words.every((w) => hay.includes(w));
}

export interface PastOptions {
  filter?: PastFilter;
  /** Show calendar holds with nobody else in them ("Block", "Focus time"). */
  showBlocks?: boolean;
  search?: string;
  book?: NameBook;
  now?: Date;
}

/** The past meetings the list shows, newest first. */
export function pastMeetings(all: readonly Meeting[], opts: PastOptions = {}): Meeting[] {
  const now = opts.now ?? new Date();
  const filter = opts.filter ?? "all";
  const book = opts.book ?? new Map<string, string>();
  return all
    .filter((m) => isPast(m, now))
    .filter((m) => opts.showBlocks || !isBlock(m))
    .filter((m) => {
      if (filter === "notes") return hasNotes(m);
      if (filter === "none") return !hasNotes(m) && !isBlock(m);
      if (filter === "actions") return openActionCount(m) > 0;
      return true;
    })
    .filter((m) => matchesSearch(m, opts.search ?? "", book))
    .sort(byStartDesc);
}

/** How many own blocks the switch would add back (for its hint). */
export function blockCount(all: readonly Meeting[], now: Date = new Date()): number {
  return all.filter((m) => isPast(m, now) && isBlock(m)).length;
}

export interface DayGroup {
  key: string;
  /** "Today", "Yesterday", "Tue 27 Oct"; "No date". */
  label: string;
  meetings: Meeting[];
}

/** Rows under day headings, in the order given (newest first). */
export function byDay(list: readonly Meeting[], now: Date = new Date()): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const m of list) {
    const d = toDate(m.start_at ?? m.created_at);
    const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : "none";
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      g = { key, label: d ? formatDay(d, now) : "No date", meetings: [] };
      groups.push(g);
    }
    g.meetings.push(m);
  }
  return groups;
}

export type MarkerTone = "notes" | "quiet" | "info" | "warning" | "danger";

export interface PastMarker {
  label: string;
  tone: MarkerTone;
  hint?: string;
}

/**
 * The quiet words on the right of a row, at most two, only for what the
 * meeting HAS: its notes (a recording on its way or failed says that
 * instead) and its open actions. A meeting without notes says nothing (it
 * is the usual case, and the "No notes" filter finds them); whether the
 * agent asked about it is the one line above the list, never the row's.
 */
export function pastMarkers(m: Meeting, opts: { now?: Date } = {}): PastMarker[] {
  const now = opts.now ?? new Date();
  const out: PastMarker[] = [];
  if (m.status === "skipped" && m.skip_reason === "booker_cancelled") {
    return [{ label: "Cancelled by the booker", tone: "quiet" }];
  }
  if (uploadStalled(m, now)) out.push({ label: "Upload paused", tone: "warning", hint: "No audio has come in for a while; what arrived is finished and written up on its own" });
  else if (m.recording_status === "uploading") out.push({ label: "Recording", tone: "info" });
  else if (m.recording_status === "transcribing") out.push({ label: "Transcribing", tone: "info", hint: "The notes follow the transcript" });
  else if (meetingRecordingFailed(m)) out.push({ label: "Transcription failed", tone: "danger", hint: m.recording_error ?? undefined });
  else if (hasNotes(m)) out.push({ label: "Notes", tone: "notes" });
  else if (m.transcript_path || m.recording_status === "ready") out.push({ label: "Transcript, no notes yet", tone: "quiet" });
  const open = openActionCount(m);
  if (open) out.push({ label: pluralize(open, "open action"), tone: "quiet" });
  return out;
}

// ─── which tab /meetings opens on ───

/** ?tab values that are Past (needs, done and dismissed are the old tabs it replaced). */
export const PAST_TABS: ReadonlySet<string> = new Set(["past", "needs", "done", "dismissed"]);

/**
 * The tab the page opens on. An explicit ?tab wins (upcoming and calendar
 * are the Calendar). With none, Past while meetings wait on the operator
 * (`waiting`: the line above the Past list has something to say): the
 * sidebar's count, Home's "N meetings waiting on you" and the bell's notes
 * all link to bare /meetings, and only Past shows what they count. Else
 * the Calendar.
 */
export function landingTab(tab: string | null, waiting: boolean): "calendar" | "past" {
  if (tab) return PAST_TABS.has(tab) ? "past" : "calendar";
  return waiting ? "past" : "calendar";
}

// ─── the one line above the list ───

export interface NoNotesLine {
  /** Asked about in chat in the last days, no notes yet (meetingAskedRecently, a failed recording aside). */
  asked: Meeting[];
  /** A failed recording not dismissed (meetingRecordingFailed). */
  failed: Meeting[];
}

/** asked + failed = the shared "waits on you" count (attention/predicates), the sidebar's number. */
export function noNotesLine(all: readonly Meeting[], now: Date = new Date()): NoNotesLine {
  const failed = all.filter((m) => meetingRecordingFailed(m)).sort(byStartDesc);
  const asked = all.filter((m) => meetingAskedRecently(m, now) && !meetingRecordingFailed(m)).sort(byStartDesc);
  return { asked, failed };
}

/** "Julia asked in chat about 7 recent meetings with no notes. 1 recording couldn't be transcribed." */
export function noNotesWords(line: NoNotesLine, agentName: string): string {
  const parts: string[] = [];
  const n = line.asked.length;
  if (n) parts.push(`${agentName || "Your agent"} asked in chat about ${pluralize(n, "recent meeting")} with no notes.`);
  const f = line.failed.length;
  if (f) parts.push(`${pluralize(f, "recording")} couldn’t be transcribed.`);
  return parts.join(" ");
}

/** The ids the line is about, for "hide until a new one comes". */
export function lineIds(line: NoNotesLine): string[] {
  return [...line.asked, ...line.failed].map((m) => m.id).sort();
}

/** Shown unless every meeting it names was there when the operator hid it. */
export function lineShown(line: NoNotesLine, hidden: readonly string[]): boolean {
  const ids = lineIds(line);
  if (!ids.length) return false;
  const seen = new Set(hidden);
  return ids.some((id) => !seen.has(id));
}

// ─── asking the agent ───

/** "Catchup" (Tue 27 Oct, 17:00), for a chat message. */
function meetingRef(m: Meeting, now: Date): string {
  const at = formatDateTime(m.start_at ?? m.created_at, {}, now);
  return `"${meetingTitle(m)}"${at ? ` (${at})` : ""}`;
}

/** The message "Ask <agent> to prepare" hands to the chat (sent only when the operator presses Send). */
export function prepareDraft(m: Meeting, book: NameBook, now: Date = new Date()): string {
  const who = peopleIn(m, book);
  const withWho = who.length ? ` with ${joinList(who.slice(0, 4))}${who.length > 4 ? " and others" : ""}` : "";
  return `Brief me for ${meetingRef(m, now)}${withWho}: who they are, what we last discussed and what I should ask.`;
}

/** The message "Tell <agent> what happened" starts; the operator writes the rest. */
export function notesDraft(m: Meeting, now: Date = new Date()): string {
  return `Notes for ${meetingRef(m, now)}: `;
}

/** The agent's chat with a message ready to send: the desktop page or the phone's thread. */
export function chatDraftHref(agentTemplate: string, text: string, companion = false): string {
  const t = encodeURIComponent(agentTemplate);
  const draft = encodeURIComponent(text);
  return companion ? `/companion/chat/${t}?draft=${draft}` : `/agents/${t}/chat?draft=${draft}`;
}
